import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { Show, UserButton, useAuth } from '@clerk/react'
import { Link } from 'react-router-dom'
import { getActivePod } from '@/lib/pod'
import StaffSignIn from '@/components/StaffSignIn'
import WeeklyReportCard from '@/components/WeeklyReportCard'
import NeedsConfirmingCard from '@/components/NeedsConfirmingCard'
import { isOwnerClaim } from '@/lib/staff-role'
import { saveBlob } from '@/lib/save-file'
import { fetchAudit, AuditError, type GapAudit } from '@/lib/audit'
import {
  auditCsv,
  auditCsvName,
  auditRows,
  formatDayRange,
  recordsNote,
  summariseAudit,
  CAPPED_NOTE,
} from '../../workers/audit-format'

/**
 * THE KELVO OWNER'S REPORT — /report on a staff build (Build 10c).
 *
 * WHAT STAFF ASKED THAT WAS NOT WRITTEN DOWN. Over the days he picks (the last
 * fortnight to start with): how many questions staff asked, how many the
 * written-down answers covered, how many were not written down, how many of
 * those were about keeping people safe, and where each of those stands now.
 * It is his to-do list and the product's proof — "first fortnight: 23
 * questions, 9 not written down, 3 about safety" — so it has a sentence to
 * copy and a CSV to keep.
 *
 * THIS PAGE CHOOSES NO WORDS. The sentence, the rows, the notes, the CSV and
 * its name all come from workers/audit-format.ts, which has a suite; the
 * numbers come from the engine, derived at read time (workers/audit.ts).
 *
 * THE WEEKLY TEXT LIVES HERE TOO: the same switch as Foredesk's dashboard
 * (src/components/WeeklyReportCard.tsx), because this is where "See it all"
 * in that text lands — and the same re-confirmation queue
 * (src/components/NeedsConfirmingCard.tsx), because the text's "N things need
 * confirming" must lead somewhere he can confirm them. It shows nothing when
 * nothing is due.
 *
 * OWNER ONLY, ENFORCED BY THE WORKER. This page hides itself from anyone whose
 * session does not say owner, as a courtesy; the owner gate refuses every
 * /api/admin/* call from anyone else regardless.
 */
const INPUT =
  'mt-1 w-full rounded-lg border border-border-subtle bg-surface-raised px-3 py-2 text-sm text-text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 disabled:opacity-50'
const QUIET =
  'rounded-lg border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-primary transition-colors hover:border-brand-accent disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2'

export default function OwnerReport() {
  const pod = getActivePod()
  const { isLoaded, sessionClaims } = useAuth()

  useEffect(() => {
    const previous = document.title
    document.title = `${pod.brandName} — report`
    return () => {
      document.title = previous
    }
  }, [pod.brandName])

  if (!isLoaded) {
    return (
      <div className="flex min-h-[100svh] items-center justify-center bg-surface-base">
        <p className="text-sm text-text-muted" role="status">
          Loading…
        </p>
      </div>
    )
  }

  return (
    <>
      <Show when="signed-out">
        <StaffSignIn returnTo="/report" />
      </Show>

      <Show when="signed-in">
        <div className="min-h-[100svh] bg-surface-base">
          <header className="border-b border-border-subtle bg-surface-raised">
            <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-3">
              <div className="min-w-0">
                <p className="font-display font-semibold leading-tight tracking-tight truncate">{pod.brandName}</p>
                <p className="text-xs text-text-muted leading-tight">Report</p>
              </div>
              <div className="flex items-center gap-3">
                <Link
                  to="/"
                  className="rounded-lg border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-primary transition-colors hover:border-brand-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
                >
                  Back to {pod.assistantName}
                </Link>
                <UserButton />
              </div>
            </div>
          </header>

          <main className="mx-auto max-w-3xl px-4 py-8">
            {isOwnerClaim(sessionClaims) ? (
              <>
                <WeeklyReportCard />
                <NeedsConfirmingCard audience="staff" />
                <AuditSection />
              </>
            ) : (
              <section className="rounded-card border border-border-subtle bg-surface-raised p-6">
                <h1 className="font-display text-lg font-semibold tracking-tight">This page is for the owner</h1>
                <p className="mt-2 text-sm text-text-muted">
                  The report on {pod.brandName} is for the owner. If you think you should have access, speak to the
                  office.
                </p>
              </section>
            )}
          </main>
        </div>
      </Show>
    </>
  )
}

function AuditSection() {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  const [report, setReport] = useState<GapAudit | null>(null)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)

  const load = useCallback(async (days: { from?: string; to?: string }) => {
    setLoading(true)
    setError('')
    setCopied(false)
    try {
      const next = await fetchAudit(await getTokenRef.current(), days)
      setReport(next)
      // The engine says which days it used: the boxes show them, so the
      // default fortnight is visible and editable rather than implied.
      setFrom(next.fromDay)
      setTo(next.toDay)
    } catch (e) {
      setError(e instanceof AuditError ? e.message : 'Could not build the report.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load({})
  }, [load])

  function submit(e: FormEvent) {
    e.preventDefault()
    void load({ from, to })
  }

  async function copySentence() {
    if (!report) return
    try {
      await navigator.clipboard.writeText(summariseAudit(report))
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  const note = report ? recordsNote(report) : null

  return (
    <section>
      <h1 className="font-display text-2xl font-semibold tracking-tight">What was not written down</h1>
      <p className="mt-1 max-w-prose text-sm text-text-muted">
        What your staff asked, how much of it your written-down answers covered, and what they could not find — with
        where each of those stands now.
      </p>

      <form onSubmit={submit} className="mt-5 flex flex-wrap items-end gap-3">
        <label className="block text-xs font-medium text-text-primary">
          From
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} disabled={loading} className={INPUT} />
        </label>
        <label className="block text-xs font-medium text-text-primary">
          To
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} disabled={loading} className={INPUT} />
        </label>
        <button type="submit" disabled={loading} className={QUIET}>
          {loading ? 'Counting…' : 'Show these days'}
        </button>
      </form>

      {error ? (
        <p role="alert" className="mt-4 text-sm text-text-danger">
          {error}
        </p>
      ) : null}

      {report && !error ? (
        <div className="mt-6 rounded-card border border-border-subtle bg-surface-raised p-5">
          <h2 className="font-display text-sm font-semibold tracking-tight text-text-primary">
            {formatDayRange(report.fromDay, report.toDay)}
          </h2>
          <p className="mt-2 text-text-primary">{summariseAudit(report)}</p>
          {note ? <p className="mt-2 text-sm text-text-muted">{note}</p> : null}
          {report.capped ? <p className="mt-2 text-sm font-medium text-text-danger">{CAPPED_NOTE}</p> : null}

          <table className="mt-4 w-full text-sm">
            <tbody>
              {auditRows(report).map((row) => (
                <tr key={row.label} className="border-t border-border-subtle">
                  <th scope="row" className="py-2 pr-4 text-left font-normal text-text-muted">
                    {row.label}
                  </th>
                  <td className="py-2 text-right font-medium tabular-nums text-text-primary">{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {report.safety !== null ? (
            <details className="mt-3 text-xs text-text-muted">
              <summary className="cursor-pointer">How “about safety” is decided</summary>
              <p className="mt-1">
                A question counts as about safety when it uses one of these words: {report.safetyTerms.join(', ')}. A
                question that uses none of them is not counted, so this can miss one, but it never counts one that is
                not.
              </p>
            </details>
          ) : null}

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button onClick={() => void copySentence()} className={QUIET}>
              {copied ? 'Copied' : 'Copy the sentence'}
            </button>
            <button
              onClick={() => saveBlob(new Blob([auditCsv(report)], { type: 'text/csv;charset=utf-8' }), auditCsvName(report))}
              className={QUIET}
            >
              Download as a spreadsheet (CSV)
            </button>
            {report.gaps.waiting > 0 ? (
              <Link to="/questions" className="text-sm text-text-primary underline underline-offset-2">
                Answer what is waiting
              </Link>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  )
}
